import { ActionIcon, Alert, Badge, Box, Button, Checkbox, Divider, Grid, Group, Loader, Paper, Progress, Radio, Stack, Text, ThemeIcon, Title, UnstyledButton } from '@mantine/core';
import { showNotification } from '@mantine/notifications';
import React, { useEffect, useState } from 'react';
import { FaArrowLeft, FaCalendarAlt, FaCheck, FaCreditCard, FaLongArrowAltRight, FaReceipt, FaTimes } from 'react-icons/fa';
import InputField, { InputFieldTypes } from '../components/input_field';
import { PaymentForm } from '../components/payment_form';
import { useApp } from '../contexts/app.context';
import { useAuth } from '../contexts/auth.context';
import { useNoCaptcha } from '../hooks/useNoCaptcha';
import { useTimer } from '../hooks/useTimer';
import { SEARCH_COUPON } from '../services/auth';
import { SAVE_COMPANY_PLAN } from '../services/companies';
import { GET_BILLING_DETAILS, GET_CHECKOUT_PREVIEW, START_PIX } from '../services/payments';
import { GET_ALL_PLANS } from '../services/plans';
import { extenseRecurrenceTypeDescription, formatMoney, useQuery } from '../utility/util';
import QRCode from 'react-qr-code';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

const toNumber = (value: any, fallback = 0) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : fallback;
};

const clamp = (value: number, min: number, max: number) => {
    return Math.min(Math.max(value, min), max);
};

const getRecurrenceMultiplier = (recurrenceType: any, recurrencePeriod: any) => {
    const base = recurrenceType === 'year' ? 12 : 1;
    return base * Math.max(toNumber(recurrencePeriod, 1), 1);
};

const applyCoupon = (cycleValue: number, coupon: any) => {
    if (!coupon) return Math.max(cycleValue, 0);

    const couponValue = toNumber(coupon?.value, 0);
    if (coupon?.type === 'static') return Math.max(cycleValue - couponValue, 0);

    const percentageDiscount = clamp(couponValue, 0, 100);
    return Math.max(cycleValue * ((100 - percentageDiscount) / 100), 0);
};

const calcularValorCiclo = ({
    plan,
    customers,
    storage,
    coupon,
}: {
    plan: any;
    customers: any;
    storage: any;
    coupon: any;
}) => {
    if (!plan) return 0;

    const recurrenceMultiplier = getRecurrenceMultiplier(plan?.recurrence_type, plan?.recurrence_period);
    const basePrice = toNumber(plan?.price, 0);
    const planMaxCustomers = toNumber(plan?.max_customers, 0);
    const planMaxStorage = toNumber(plan?.max_storage, 0);
    const addOnCustomer = toNumber(plan?.add_on_customer, 0);
    const addOnStorage = toNumber(plan?.add_on_storage, 0);

    const safeCustomers = Math.max(toNumber(customers, 0), 0);
    const safeStorage = Math.max(toNumber(storage, 0), 0);

    const additionalCustomers = Math.max(safeCustomers - planMaxCustomers, 0);
    const additionalStorage = Math.max(safeStorage - planMaxStorage, 0);

    const cycleBaseValue = basePrice * recurrenceMultiplier;
    const cycleCustomersValue = addOnCustomer * additionalCustomers * recurrenceMultiplier;
    const cycleStorageValue = addOnStorage * additionalStorage * recurrenceMultiplier;
    const cycleSubtotal = cycleBaseValue + cycleCustomersValue + cycleStorageValue;

    return applyCoupon(cycleSubtotal, coupon);
};

const subtractRecurrenceFromDate = (endDate: Date, recurrenceType: any, recurrencePeriod: any) => {
    const period = Math.max(toNumber(recurrencePeriod, 1), 1);
    const start = new Date(endDate);

    if (recurrenceType === 'year') start.setFullYear(start.getFullYear() - period);
    else start.setMonth(start.getMonth() - period);

    return start;
};

const addRecurrenceToDate = (startDate: Date, recurrenceType: any, recurrencePeriod: any) => {
    const period = Math.max(toNumber(recurrencePeriod, 1), 1);
    const end = new Date(startDate);

    if (recurrenceType === 'year') end.setFullYear(end.getFullYear() + period);
    else end.setMonth(end.getMonth() + period);

    return end;
};

const calcularProporcaoRestante = ({
    activeUntil,
    payments,
    recurrenceType,
    recurrencePeriod,
    now,
}: {
    activeUntil: any;
    payments: any[];
    recurrenceType: any;
    recurrencePeriod: any;
    now: Date;
}) => {
    const cycleEndDate = activeUntil ? new Date(activeUntil) : null;
    if (!cycleEndDate || Number.isNaN(cycleEndDate.getTime())) {
        return {
            remainingRatio: 0,
            remainingDays: 0,
            cycleDays: 0,
            cycleStart: now,
            cycleEnd: now,
        };
    }

    const validPayments = (payments ?? [])
        .filter((payment) => payment?.payed_at && !payment?.refund_at)
        .sort((a, b) => new Date(b.payed_at).getTime() - new Date(a.payed_at).getTime());

    const lastValidPaymentAt = validPayments[0]?.payed_at ? new Date(validPayments[0].payed_at) : null;
    const fallbackCycleStart = subtractRecurrenceFromDate(cycleEndDate, recurrenceType, recurrencePeriod);
    const cycleStartDate = lastValidPaymentAt && !Number.isNaN(lastValidPaymentAt.getTime()) ? lastValidPaymentAt : fallbackCycleStart;

    const cycleMs = Math.max(cycleEndDate.getTime() - cycleStartDate.getTime(), 0);
    const remainingMs = Math.max(cycleEndDate.getTime() - now.getTime(), 0);

    const remainingRatio = cycleMs > 0 ? clamp(remainingMs / cycleMs, 0, 1) : 0;
    const remainingDays = Math.max(Math.ceil(remainingMs / DAY_IN_MS), 0);
    const cycleDays = Math.max(Math.ceil(cycleMs / DAY_IN_MS), 0);

    return {
        remainingRatio,
        remainingDays,
        cycleDays,
        cycleStart: cycleStartDate,
        cycleEnd: cycleEndDate,
    };
};

const calcularCheckoutPricing = ({
    activeUntil,
    currentPlan,
    targetPlan,
    currentCustomers,
    targetCustomers,
    currentStorage,
    targetStorage,
    coupon,
    payments,
    now = new Date(),
}: {
    activeUntil: any;
    currentPlan: any;
    targetPlan: any;
    currentCustomers: any;
    targetCustomers: any;
    currentStorage: any;
    targetStorage: any;
    coupon: any;
    payments: any[];
    now?: Date;
}) => {
    const cycleEndDate = activeUntil ? new Date(activeUntil) : null;
    const isActiveSubscription = !!cycleEndDate && cycleEndDate.getTime() > now.getTime();
    const pricingMode = isActiveSubscription ? 'prorated_upgrade' : 'new_cycle';

    const cycleValueCurrent = calcularValorCiclo({
        plan: currentPlan,
        customers: currentCustomers,
        storage: currentStorage,
        coupon,
    });

    const cycleValueTarget = calcularValorCiclo({
        plan: targetPlan,
        customers: targetCustomers,
        storage: targetStorage,
        coupon,
    });

    const cycleDelta = Math.max(cycleValueTarget - cycleValueCurrent, 0);

    const ratioData = calcularProporcaoRestante({
        activeUntil,
        payments,
        recurrenceType: currentPlan?.recurrence_type ?? targetPlan?.recurrence_type,
        recurrencePeriod: currentPlan?.recurrence_period ?? targetPlan?.recurrence_period,
        now,
    });

    const chargeValue = pricingMode === 'prorated_upgrade'
        ? cycleDelta * ratioData.remainingRatio
        : cycleValueTarget;

    const roundedCharge = Number(chargeValue.toFixed(2));
    const amountCents = Number.parseInt(roundedCharge.toFixed(2).replace(/[.,]/g, ''), 10);

    const previewActiveUntil = pricingMode === 'prorated_upgrade'
        ? activeUntil
        : addRecurrenceToDate(now, targetPlan?.recurrence_type, targetPlan?.recurrence_period).toISOString();

    return {
        pricingMode,
        active_until: previewActiveUntil,
        cycleValueCurrent,
        cycleValueTarget,
        cycleDelta,
        remainingRatio: ratioData.remainingRatio,
        remainingDays: ratioData.remainingDays,
        cycleDays: ratioData.cycleDays,
        chargeValue: Math.max(roundedCharge, 0),
        amountCents: Number.isFinite(amountCents) ? Math.max(amountCents, 0) : 0,
        nextCycleValue: Math.max(cycleValueTarget, 0),
    };
};

export default function ChoosePlan({ onClose = null, initialData = null }) {
    const [loadingPlans, setLoadingPlans] = useState(false);
    const [loadingSave, setLoadingSave] = useState(false);
    const [loadingPix, setLoadingPix] = useState(false);
    const [plans, setPlans] = useState<any[]>([]);
    const [formData, setFormData] = useState<any>({});
    const [searchCoupon, setSearchCoupon] = useState<string>("");
    const [selectedRecurrencyType, setSelectedRecurrencyType] = useState<string>("1-year");
    const [coupons, setCoupons] = useState<any>({});
    const [billing, setBilling] = useState<any>(null);
    const [checkoutPreview, setCheckoutPreview] = useState<any>(null);
    const [loadingPreview, setLoadingPreview] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [paymentMethod, setPaymentMethod] = useState<any>("credit");
    const [pix, setPix] = useState<any>(null);
    const [checkoutStep, setCheckoutStep] = useState<'review' | 'payment'>("review");
    const [termsAccepted, setTermsAccepted] = useState(false);

    const selectedPlan = plans.find(s => s._id === formData?.plan);

    const { startPlan, socket } = useApp();
    const { userData, loadUserData } = useAuth();
    const { getNoCaptcha } = useNoCaptcha();
    const { timeLeft } = useTimer({
        expiresAt: pix?.expires_at, expiresIn: pix?.expires_in, onExpired: () => {
            startPix();
        }
    });
    const query = useQuery();
    const cupom = query.get("cupom");
    const prefillSignature = JSON.stringify(initialData ?? {});

    let billingFileSize = toNumber(billing?.filesSize, 0);
    let planMaxStorage = toNumber(selectedPlan?.max_storage, 0);
    let minStorage = billingFileSize > planMaxStorage ? billingFileSize : planMaxStorage;
    let formStorage = toNumber(formData?.storage, 0);
    if (minStorage < planMaxStorage) minStorage = planMaxStorage;
    minStorage = Math.ceil((minStorage / 1000)) * 1000;
    let extraStorage = formStorage - planMaxStorage;

    let billingCustomers = toNumber(billing?.customers, 0);
    let planMaxCustomers = toNumber(selectedPlan?.max_customers, 0);
    let minCustomers = billingCustomers > planMaxCustomers ? billingCustomers : planMaxCustomers;
    const selectedCoupon = coupons[searchCoupon] ?? null;
    const previewAvailable = !!checkoutPreview && !previewError;
    const shouldBlockCheckout = loadingPreview || !previewAvailable;
    const previewProration = checkoutPreview?.proration ?? {};

    const formatDate = (value: any) => {
        if (!value) return "-";
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return "-";
        return date.toLocaleDateString("pt-BR");
    };

    const pricingMode = checkoutPreview?.pricingMode ?? null;
    const isProratedUpgrade = pricingMode === 'prorated_upgrade';
    const cycleCurrentValue = toNumber(previewProration?.cycleValueCurrent, 0);
    const cycleTargetValue = toNumber(previewProration?.cycleValueTarget, toNumber(checkoutPreview?.planValue, 0));
    const cycleDifferenceValue = toNumber(previewProration?.cycleDelta, 0);
    const remainingRatio = toNumber(previewProration?.remainingRatio, 0);
    const remainingDays = toNumber(previewProration?.remainingDays, 0);
    const cycleDays = toNumber(previewProration?.cycleDays, 0);
    const previewNowValue = toNumber(checkoutPreview?.value, 0);
    const prorationChargeValue = toNumber(previewProration?.chargeValue, previewNowValue);
    const chargeNow = isProratedUpgrade ? prorationChargeValue : previewNowValue;
    const amountCents = toNumber(checkoutPreview?.amount, 0);
    const isZeroCharge = chargeNow <= 0;
    const nextCycleValue = toNumber(checkoutPreview?.planValue, cycleTargetValue);
    const activeUntil = checkoutPreview?.active_until ?? billing?.active_until;

    const recurrenceLabel = `${selectedPlan?.recurrence_period ?? 1} ${extenseRecurrenceTypeDescription(selectedPlan?.recurrence_type ?? "month", (selectedPlan?.recurrence_period ?? 1) > 1).toLowerCase()}`;
    const fidelityLabel = selectedPlan?.recurrence_type === "year"
        ? `${(selectedPlan?.recurrence_period ?? 1) * 12} meses`
        : recurrenceLabel;
    const cycleTotalLabel = selectedPlan?.recurrence_type === "year" ? 'Valor anual do pacote alvo' : 'Valor do pacote alvo no ciclo';
    const packageSummary = [
        selectedPlan?.title,
        formData?.customers > 0 ? `${formData?.customers} cliente${formData?.customers > 1 ? 's' : ''}` : null,
        formData?.storage > 0 ? `${formData?.storage / 1000}GB` : null,
    ].filter(nn => nn).join(' • ');
    const reviewMessage = isProratedUpgrade
        ? 'Ajuste proporcional até o fim do ciclo atual.'
        : 'Início de novo ciclo.';
    const reviewTitle = isProratedUpgrade ? 'Revisão do upgrade proporcional' : 'Revisão da contratação';
    const reviewDescription = isProratedUpgrade
        ? 'Confira o ajuste proporcional até o fim do ciclo e o valor do próximo vencimento.'
        : 'Confira o início do novo ciclo e o valor integral a ser cobrado.';
    const topReviewTitle = isProratedUpgrade ? 'Revise o ajuste proporcional antes de pagar' : 'Revise os detalhes do novo ciclo antes de pagar';
    const topReviewDescription = isProratedUpgrade
        ? 'Este pagamento não altera o fim do ciclo atual. A próxima renovação segue o valor completo da nova configuração.'
        : 'A cobrança atual inicia um novo ciclo com valor integral da configuração selecionada.';
    const paymentDescription = isZeroCharge
        ? 'Não haverá cobrança imediata. Ao confirmar, a atualização será aplicada e o próximo ciclo seguirá a nova configuração.'
        : 'Escolha a forma de pagamento para concluir a atualização do plano.';
    const summaryTitle = isProratedUpgrade ? 'Resumo do upgrade proporcional' : 'Resumo do novo ciclo';
    const termsLabel = isProratedUpgrade
        ? 'Entendi que este pagamento cobre apenas o ajuste proporcional até o fim do ciclo atual, sem renovação da data de vencimento.'
        : 'Entendi que este pagamento inicia um novo ciclo com cobrança integral da configuração selecionada.';

    const formatCurrencyFromPreview = (value: number) => {
        if (!previewAvailable) return '--';
        return formatMoney(toNumber(value, 0));
    };

    const getPlanTotalPrice = (plan) => {
        let value = parseFloat(plan?.price ?? 0);
        return value * {
            "month": 1,
            "year": 12,
        }[plan?.recurrence_type ?? "month" as string] * (plan?.recurrence_period ?? 1);
    };

    const loadBilling = () => {
        GET_BILLING_DETAILS()
            .then((dt) => {
                setBilling(dt);
            })
            .catch((err) => {
                showNotification({ message: err.message, color: "red" });
            });
    };

    const startPix = async () => {
        if (chargeNow <= 0 || !previewAvailable) return;
        setLoadingPix(true);
        try {
            const data = await START_PIX({
                params: { ...formData, coupon: coupons[searchCoupon], payment_type: "start_plan" },
            });
            setPix(data);
        } catch (err) {
            showNotification({ message: err.message, color: "red" });
        } finally {
            setLoadingPix(false);
        }
    };

    const handleAdvanceToPayment = () => {
        if (shouldBlockCheckout) {
            showNotification({
                message: previewError ?? 'Aguarde o cálculo do preview para continuar.',
                color: 'yellow',
            });
            return;
        }

        if (!termsAccepted) {
            showNotification({
                message: isProratedUpgrade
                    ? "Confirme que entendeu os termos do upgrade proporcional antes de continuar."
                    : "Confirme que entendeu os detalhes do novo ciclo antes de continuar.",
                color: "yellow",
            });
            return;
        }

        setCheckoutStep("payment");
    };

    const finishSave = async () => {
        if (shouldBlockCheckout) {
            showNotification({
                message: previewError ?? 'Aguarde o cálculo do preview para finalizar.',
                color: 'yellow',
            });
            return;
        }

        if (!termsAccepted) {
            showNotification({
                message: isProratedUpgrade
                    ? "Confirme os termos do upgrade proporcional antes de finalizar."
                    : "Confirme os termos do novo ciclo antes de finalizar.",
                color: "yellow"
            });
            return;
        }

        setLoadingSave(true);
        try {
            const captchaToken = await getNoCaptcha("subscription");
            await SAVE_COMPANY_PLAN({ ...formData, coupon: coupons[searchCoupon], captchaToken });
            startPlan(false);
            loadUserData();
        } catch (err) {
            showNotification({ message: err.message, color: "red" });
        } finally {
            setLoadingSave(false);
        }
    };

    const loadPlans = () => {
        setLoadingPlans(true);

        GET_ALL_PLANS()
            .then(res => {
                setPlans(res);
                setLoadingPlans(false);
            })
            .catch(err => {
                setLoadingPlans(false);
                showNotification({ message: err.message, color: "red" });
            });
    };

    React.useEffect(() => {
        if (socket && pix?.id) {
            const func = ({ }) => {
                startPlan(false);
                loadUserData();
            };
            socket.on(`order-updated-${pix.id}`, func);
            return () => {
                socket.removeAllListeners(`order-updated-${pix.id}`);
            };
        }
    }, [socket, pix]);

    useEffect(() => {
        loadPlans();
        loadBilling();
    }, []);

    useEffect(() => {
        if (!selectedPlan?._id) {
            setCheckoutPreview(null);
            setPreviewError(null);
            setLoadingPreview(false);
            return;
        }

        const timeoutId = setTimeout(() => {
            setLoadingPreview(true);
            setPreviewError(null);

            GET_CHECKOUT_PREVIEW({
                plan: selectedPlan?._id,
                customers: toNumber(formData?.customers, 0) || undefined,
                storage: toNumber(formData?.storage, 0) || undefined,
                coupon: selectedCoupon?._id || undefined,
            })
                .then((preview) => {
                    setCheckoutPreview(preview);
                })
                .catch(() => {
                    setCheckoutPreview(null);
                    setPreviewError('Não foi possível calcular o valor atualizado. Tente novamente.');
                })
                .finally(() => {
                    setLoadingPreview(false);
                });
        }, 300);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [selectedPlan?._id, formData?.customers, formData?.storage, selectedCoupon?._id, searchCoupon]);

    useEffect(() => {
        if (checkoutStep === "payment" && paymentMethod === "pix" && selectedPlan && chargeNow > 0 && previewAvailable) startPix();
        else setPix(null);
    }, [paymentMethod, checkoutStep, selectedPlan, chargeNow, previewAvailable]);

    useEffect(() => {
        setFormData(fd => ({ ...fd, installments: selectedPlan?.max_installments ?? "1" }));
    }, [selectedPlan]);

    useEffect(() => {
        if (!selectedPlan?._id) return;

        setCheckoutStep("review");
        setTermsAccepted(false);
        setPaymentMethod("credit");
        setCheckoutPreview(null);
        setPreviewError(null);
    }, [selectedPlan?._id]);

    useEffect(() => {
        if (cupom && cupom.length > 3) setSearchCoupon(cupom);
    }, [cupom]);

    useEffect(() => {
        if (searchCoupon.length > 3) SEARCH_COUPON(searchCoupon)
            .then(res => {
                setCoupons(c => ({ ...c, [searchCoupon]: res }));
            })
            .catch(() => {
            });
    }, [searchCoupon]);

    useEffect(() => {
        setFormData(fd => ({
            ...fd,
            customers: Math.max(parseInt(`${fd?.customers ?? minCustomers}`) || minCustomers, minCustomers),
            storage: Math.max(parseInt(`${fd?.storage ?? minStorage}`) || minStorage, minStorage),
        }));
    }, [billing, minStorage, minCustomers]);

    useEffect(() => {
        if (!initialData || !plans.length) return;

        const incomingPlanId = initialData?.planId;
        const incomingPlanTitle = (initialData?.planTitle ?? "").toLowerCase();
        const incomingPlan = plans.find(p => p?._id === incomingPlanId)
            || plans.find(p => (p?.title ?? "").toLowerCase() === incomingPlanTitle);

        if (!incomingPlan?._id) return;

        setFormData(fd => ({
            ...fd,
            plan: incomingPlan._id,
        }));
    }, [plans, prefillSignature]);

    useEffect(() => {
        if (!selectedPlan?._id || !initialData) return;

        const desiredCustomers = parseInt(`${initialData?.targetCustomers ?? 0}`) || 0;
        if (desiredCustomers > 0) {
            setFormData(fd => ({
                ...fd,
                customers: Math.max(desiredCustomers, minCustomers),
            }));
        }

        if (initialData?.openStep === "payment") setCheckoutStep("payment");
        else setCheckoutStep("review");
        setTermsAccepted(false);
    }, [selectedPlan?._id, prefillSignature, minCustomers]);

    return <Box p="md">
        {!selectedPlan && <>
            <Group align='flex-start'>
                <div style={{ flex: 1 }}>
                    <Title order={3}>Escolha Seu Plano</Title>
                </div>
                {onClose && <ActionIcon onClick={onClose} variant='outline'>
                    <FaTimes />
                </ActionIcon>}
            </Group>

            <Group pb="md" pt="md" style={{ justifyContent: 'center' }} align='center'>
                <Group spacing={5} style={{ borderRadius: 40, padding: '10px', border: '1px solid #DFDFDF', whiteSpace: 'nowrap' }}>
                    {
                        plans
                            .filter(p => `${(p.recurrence_period ?? 1)}-${(p.recurrence_type ?? "month")}` === "1-year")
                            .length > 0 &&
                        <Button
                            style={{ borderRadius: 20, width: 100, border: selectedRecurrencyType === "1-year" ? '1px solid #DFDFDF' : 0 }}
                            onClick={() => setSelectedRecurrencyType("1-year")}
                            variant={selectedRecurrencyType === "1-year" ? "filled" : "light"} size="xs">Anual</Button>}

                    {
                        plans
                            .filter(p => `${(p.recurrence_period ?? 1)}-${(p.recurrence_type ?? "month")}` === "6-month")
                            .length > 0 &&
                        <Button
                            style={{ borderRadius: 20, width: 100, border: selectedRecurrencyType === "6-month" ? '1px solid #DFDFDF' : 0 }}
                            onClick={() => setSelectedRecurrencyType("6-month")}
                            variant={selectedRecurrencyType === "6-month" ? "filled" : "light"} size="xs">Semestral</Button>}

                    {
                        plans
                            .filter(p => `${(p.recurrence_period ?? 1)}-${(p.recurrence_type ?? "month")}` === "3-month")
                            .length > 0 &&
                        <Button
                            style={{ borderRadius: 20, width: 100, border: selectedRecurrencyType === "3-month" ? '1px solid #DFDFDF' : 0 }}
                            onClick={() => setSelectedRecurrencyType("3-month")}
                            variant={selectedRecurrencyType === "3-month" ? "filled" : "light"} size="xs">Trimestral</Button>}

                    {
                        plans
                            .filter(p => `${(p.recurrence_period ?? 1)}-${(p.recurrence_type ?? "month")}` === "1-month")
                            .length > 0 &&
                        <Button
                            style={{ borderRadius: 20, width: 100, border: selectedRecurrencyType === "1-month" ? '1px solid #DFDFDF' : 0 }}
                            onClick={() => setSelectedRecurrencyType("1-month")}
                            variant={selectedRecurrencyType === "1-month" ? "filled" : "light"} size="xs">Mensal</Button>}
                </Group>
            </Group>

            {loadingPlans && <Loader mt="xl" />}
            <Grid mt="md" mb="md">
                {plans
                    .filter(p => `${(p.recurrence_period ?? 1)}-${(p.recurrence_type ?? "month")}` === selectedRecurrencyType)
                    .sort((a, b) => a.add_on_customer > b.add_on_customer ? 1 : -1)
                    .map(plan => (
                        <Grid.Col md={4} key={plan._id}>
                            <UnstyledButton style={{ width: '100%' }} onClick={() => {
                                setFormData(fd => ({ ...fd, plan: plan._id }));
                            }}>
                                <Paper shadow='xs' p="md" style={{ borderColor: '#DFDFDF' }}>
                                    <Title order={3}>{plan.title}</Title>
                                    <Text c="gray" size="sm" mt="sm">{(plan.description ?? "").split("\n").map((d) => <div key={`${plan._id}-${d}`} style={{ minHeight: 14 }}>{d}</div>)}</Text>

                                    {
                                        (plan.max_installments ?? 1) > 1
                                            ? <Box mt="md">
                                                <Title size="22px">{plan.max_installments ?? 1} x {formatMoney((plan.price ? getPlanTotalPrice(plan) / (plan.max_installments ?? 1) : plan.add_on_customer))}</Title>
                                                <Text size="sm">ou {formatMoney((plan.price ? getPlanTotalPrice(plan) : plan.add_on_customer))} à vista</Text>
                                            </Box>
                                            : <Title mt="md" size="22px">{formatMoney((plan.price ? getPlanTotalPrice(plan) : plan.add_on_customer))}</Title>
                                    }
                                    <Button mt="md" fullWidth>Confirmar</Button>
                                </Paper>
                            </UnstyledButton>
                        </Grid.Col>
                    ))}
            </Grid>
        </>}

        {selectedPlan && <>
            <Grid>
                <Grid.Col md={12}>
                    <Group>
                        <ActionIcon size="xl" variant="outline" color="gray" onClick={() => setFormData(fd => ({ ...fd, plan: null }))}><FaArrowLeft /></ActionIcon>
                        <Title order={3}>Checkout</Title>
                    </Group>
                </Grid.Col>
                <Grid.Col md={12}>
                    <Paper shadow='xs' p="lg" style={{ border: '1px solid #EFE8D8', background: 'linear-gradient(135deg, #FFF9EC 0%, #FFFFFF 100%)' }}>
                        <Group position='apart' align='flex-start'>
                            <Box style={{ flex: 1 }}>
                                <Group spacing="xs" mb="xs">
                                    <Badge color={checkoutStep === 'review' ? 'yellow' : 'gray'} variant={checkoutStep === 'review' ? 'filled' : 'light'}>1. Revisão</Badge>
                                    <Badge color={checkoutStep === 'payment' ? 'dark' : 'gray'} variant={checkoutStep === 'payment' ? 'filled' : 'light'}>2. Pagamento</Badge>
                                </Group>
                                <Title order={4}>{topReviewTitle}</Title>
                                <Text size="sm" c="dimmed" mt="xs">{topReviewDescription}</Text>
                            </Box>
                            <Box style={{ minWidth: 180 }}>
                                <Progress value={checkoutStep === 'review' ? 50 : 100} color="dark" radius="xl" />
                                <Text size="xs" c="dimmed" style={{ textAlign: 'right', marginTop: 6 }}>Etapa {checkoutStep === 'review' ? '1 de 2' : '2 de 2'}</Text>
                            </Box>
                        </Group>
                    </Paper>
                </Grid.Col>
                <Grid.Col md={12}>
                    <Paper shadow='xs' p="xl">
                        <Group align="flex-start">
                            <Box style={{ flex: 0.6 }}>
                                <Title order={3}>{selectedPlan.title}</Title>
                                <Text size="sm">{selectedPlan.description}</Text>
                            </Box>
                            <Box style={{ flex: 0.4 }}>
                                <Group>
                                    <Box style={{ flex: 1 }}>
                                        <Group align='flex-end' spacing="1px">
                                            <Title size="22px">{formatMoney(selectedPlan.add_on_customer ?? 0)}</Title>
                                        </Group>
                                        <Text mb="md" c="gray" size="sm">Por cliente por mês</Text>
                                        <InputField
                                            title="Total de Clientes"
                                            name="customers"
                                            fieldType={InputFieldTypes.QUANTITY}
                                            value={formData.customers}
                                            min={minCustomers}
                                            onChange={({ customers }) => {
                                                let minimum = minCustomers;
                                                minimum = (minimum > 1 ? minimum : 1);
                                                if (parseInt(customers) >= minimum) {
                                                    setFormData(fd => ({ ...fd, customers }));
                                                }
                                            }}
                                            size="xs"
                                        />
                                    </Box>
                                    <Box style={{ flex: 1 }}>
                                        <Group align='flex-end' spacing="1px">
                                            <Title size="22px">{formatMoney(selectedPlan?.add_on_storage ?? 0)}</Title>
                                        </Group>
                                        <Text mb="md" c="gray" size="sm">Por GB por mês</Text>
                                        <InputField
                                            title="Armazenamento (GB)"
                                            name="storage"
                                            fieldType={InputFieldTypes.QUANTITY}
                                            value={formData.storage / 1000}
                                            min={minStorage / 1000}
                                            onChange={({ storage }) => {
                                                let minimum = minStorage / 1000;
                                                minimum = (minimum > 1 ? minimum : 1);
                                                if (parseInt(storage) >= minimum) {
                                                    setFormData(fd => ({ ...fd, storage: storage * 1000 }));
                                                }
                                            }}
                                            size="xs"
                                        />
                                    </Box>
                                </Group>

                                <InputField
                                    title={"Cupom de Desconto"}
                                    name="cp"
                                    mt="sm"
                                    size={"md"}
                                    value={searchCoupon}
                                    onChange={({ cp }) => { setSearchCoupon(cp); }}
                                    rightSection={coupons[searchCoupon] && <FaCheck />}
                                />
                            </Box>
                        </Group>
                    </Paper>
                </Grid.Col>
                <Grid.Col md={7}>
                    <Paper shadow='xs' p="xl">
                        {loadingPreview && <Alert color='dark' mb="md" title="Atualizando valores">
                            <Group spacing="xs" align='center'>
                                <Loader size="sm" />
                                <Text size="sm">Calculando preview oficial...</Text>
                            </Group>
                        </Alert>}
                        {!!previewError && <Alert color='red' mb="md" title="Preview indisponível">
                            {previewError}
                        </Alert>}

                        {checkoutStep === 'review' && <Stack spacing="lg">
                            <Group align='flex-start' spacing="sm">
                                <ThemeIcon color='yellow' variant='light' size={42} radius="xl">
                                    <FaReceipt />
                                </ThemeIcon>
                                <Box style={{ flex: 1 }}>
                                    <Title order={4}>{reviewTitle}</Title>
                                    <Text size="sm" c="dimmed" mt={4}>{reviewDescription}</Text>
                                </Box>
                            </Group>

                            <Alert color={isProratedUpgrade ? 'yellow' : 'dark'} radius="md" title={isProratedUpgrade ? 'Ajuste proporcional até o fim do ciclo atual' : 'Início de novo ciclo'}>
                                {reviewMessage}
                            </Alert>

                            <Grid>
                                <Grid.Col md={6}>
                                    <Paper p="md" shadow='xs' style={{ height: '100%', border: '1px solid #EFEFEF' }}>
                                        <Group align='flex-start' spacing="sm" noWrap>
                                            <ThemeIcon color='dark' variant='light' radius="xl"><FaCalendarAlt /></ThemeIcon>
                                            <Box>
                                                <Text size="xs" c="dimmed" tt="uppercase">Próximo vencimento</Text>
                                                <Text fw={700}>{formatDate(activeUntil)}</Text>
                                                <Text size="sm" c="dimmed" mt={4}>
                                                    {isProratedUpgrade
                                                        ? 'O upgrade proporcional não altera a data de vencimento neste pagamento.'
                                                        : 'Início de novo ciclo com valores definidos pelo preview oficial.'}
                                                </Text>
                                            </Box>
                                        </Group>
                                    </Paper>
                                </Grid.Col>
                                <Grid.Col md={6}>
                                    <Paper p="md" shadow='xs' style={{ height: '100%', border: '1px solid #EFEFEF' }}>
                                        <Group align='flex-start' spacing="sm" noWrap>
                                            <ThemeIcon color='yellow' variant='light' radius="xl"><FaReceipt /></ThemeIcon>
                                            <Box>
                                                <Text size="xs" c="dimmed" tt="uppercase">Cobrança agora</Text>
                                                <Text fw={700}>{previewAvailable ? (isZeroCharge ? 'Sem cobrança imediata' : formatCurrencyFromPreview(chargeNow)) : '--'}</Text>
                                                <Text size="sm" c="dimmed" mt={4}>Valor oficial retornado pelo endpoint de preview.</Text>
                                            </Box>
                                        </Group>
                                    </Paper>
                                </Grid.Col>
                            </Grid>

                            <Paper p="lg" shadow='xs' style={{ border: '1px solid #EFEFEF' }}>
                                <Text size="xs" c="dimmed" tt="uppercase">Detalhes do checkout</Text>
                                <Title order={5} mt={6}>{selectedPlan.title}</Title>
                                <Text size="sm" c="dimmed" mt={6}>{packageSummary}</Text>
                                <Divider my="md" />
                                <Stack spacing={10}>
                                    <Group position='apart'>
                                        <Text size="sm">Modo de preço</Text>
                                        <Badge color={isProratedUpgrade ? 'yellow' : 'dark'} variant='light'>
                                            {pricingMode ?? '--'}
                                        </Badge>
                                    </Group>
                                    <Group position='apart'>
                                        <Text size="sm">Valor do ciclo atual</Text>
                                        <Text fw={700}>{formatCurrencyFromPreview(cycleCurrentValue)}</Text>
                                    </Group>
                                    <Group position='apart'>
                                        <Text size="sm">Valor do ciclo alvo</Text>
                                        <Text fw={700}>{formatCurrencyFromPreview(cycleTargetValue)}</Text>
                                    </Group>
                                    <Group position='apart'>
                                        <Text size="sm">Diferença do ciclo</Text>
                                        <Text fw={700}>{formatCurrencyFromPreview(cycleDifferenceValue)}</Text>
                                    </Group>
                                    {isProratedUpgrade && <>
                                        <Group position='apart'>
                                            <Text size="sm">Tempo restante</Text>
                                            <Text fw={700}>{previewAvailable ? `${remainingDays} / ${cycleDays} dias` : '--'}</Text>
                                        </Group>
                                        <Group position='apart'>
                                            <Text size="sm">Percentual restante</Text>
                                            <Text fw={700}>{previewAvailable ? `${(remainingRatio * 100).toFixed(2)}%` : '--'}</Text>
                                        </Group>
                                        <Group position='apart'>
                                            <Text size="sm">Valor a cobrar agora</Text>
                                            <Text fw={700}>{previewAvailable ? (isZeroCharge ? 'Sem cobrança imediata' : formatCurrencyFromPreview(chargeNow)) : '--'}</Text>
                                        </Group>
                                    </>}
                                </Stack>
                            </Paper>

                            <Checkbox
                                checked={termsAccepted}
                                onChange={(event) => setTermsAccepted(event.currentTarget.checked)}
                                label={termsLabel}
                            />
                        </Stack>}

                        {checkoutStep === 'payment' && <>
                            <Group align='flex-start' spacing="sm">
                                <ThemeIcon color='dark' variant='light' size={42} radius="xl">
                                    <FaCreditCard />
                                </ThemeIcon>
                                <Box style={{ flex: 1 }}>
                                    <Title order={4}>Forma de Pagamento</Title>
                                    <Text size="sm" c="dimmed" mt={4}>{paymentDescription}</Text>
                                </Box>
                            </Group>

                            <Box mt="sm">
                                <Group>
                                    <Radio onClick={() => setPaymentMethod("credit")} checked={paymentMethod === "credit"} label="Cartão de Crédito" disabled={isZeroCharge || shouldBlockCheckout} />
                                    <Radio onClick={() => setPaymentMethod("pix")} checked={paymentMethod === "pix"} label="Pix" disabled={isZeroCharge || shouldBlockCheckout} />
                                </Group>
                                {shouldBlockCheckout && <Alert mt="md" color='red' title="Preview obrigatório">
                                    {previewError ?? 'Aguarde o cálculo do preview oficial antes de continuar.'}
                                </Alert>}
                                {isZeroCharge && <Alert mt="md" color='green' title="Atualização sem cobrança imediata">
                                    Ao confirmar, a assinatura é atualizada sem pagamento agora e a próxima renovação será pelo valor completo da nova configuração.
                                </Alert>}
                                {!isZeroCharge && paymentMethod === "pix" && <>
                                    {isProratedUpgrade && <Alert mt="md" color='dark' title="Pix para ajuste proporcional">
                                        Gere o QR Code para pagar apenas o valor proporcional calculado para o ciclo atual.
                                    </Alert>}
                                    {
                                        pix?.qr_code && timeLeft > 0 &&
                                        <Stack mt="md" align='center'>
                                            <QRCode value={pix?.qr_code} />
                                            <Text size="sm">QR Code válido por {timeLeft}s</Text>
                                        </Stack>
                                    }
                                    {loadingPix && <Loader mt="md" />}
                                </>}
                                {paymentMethod === "credit" && <>
                                    <PaymentForm
                                        data={formData?.payment}
                                        methods={userData?.paymentMethods}
                                        onChange={(dt) => {
                                            setFormData(fd => ({
                                                ...fd,
                                                payment: { ...fd?.payment, ...dt }
                                            }));
                                        }}
                                        onSave={(methodId, isNew = false) => {
                                            setFormData(fd => ({ ...fd, payment: { ...fd?.payment, method: methodId, push: isNew } }));
                                        }}
                                    />
                                    {(selectedPlan?.max_installments ?? 1) > 1 && <InputField
                                        name='installments'
                                        title="Parcelamento"
                                        fieldType={InputFieldTypes.SELECT}
                                        onChange={({ installments }) => setFormData(fd => ({ ...fd, installments }))}
                                        value={formData?.installments ?? "1"}
                                        options={
                                            [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
                                                .filter(i => i <= (selectedPlan?.max_installments ?? 1))
                                                .map(i => ({
                                                    label: `${i} x ${previewAvailable ? formatCurrencyFromPreview(nextCycleValue / i) : '--'}`,
                                                    value: `${i}`,
                                                }))
                                        }
                                    />}
                                </>}
                            </Box>
                        </>}
                    </Paper>
                </Grid.Col>
                <Grid.Col md={5}>
                    <Paper shadow='xs' p="xl" style={{ border: '1px solid #EFE8D8', background: '#FFFCF4' }}>
                        <Stack>
                            {loadingPreview && <Group spacing="xs" align='center'>
                                <Loader size="sm" />
                                <Text size="sm" c="dimmed">Atualizando resumo oficial...</Text>
                            </Group>}

                            <Group position='apart' align='center'>
                                <Title order={4}>{summaryTitle}</Title>
                                {termsAccepted && <Badge color='green' variant='light'>Termos confirmados</Badge>}
                            </Group>
                            <Paper p="md" shadow='xs' style={{ border: '1px solid #EFEFEF' }}>
                                <Text fw="bold" size="xs">Configuração final</Text>
                                <Text size="sm" mt="xs">{packageSummary}</Text>
                            </Paper>
                            <Paper p="md" shadow='xs' style={{ border: '1px solid #EFEFEF' }}>
                                <Stack spacing={10}>
                                    <Group position='apart' align='flex-start'>
                                        <Text size="sm">{cycleTotalLabel}</Text>
                                        <Text fw={700}>{formatCurrencyFromPreview(nextCycleValue)}</Text>
                                    </Group>
                                    <Group position='apart' align='flex-start'>
                                        <Text size="sm">Próximo vencimento</Text>
                                        <Text fw={700}>{formatDate(activeUntil)}</Text>
                                    </Group>
                                    <Divider />
                                    <Group position='apart' align='flex-start'>
                                        <Box>
                                            <Text fw={700}>Cobrança agora</Text>
                                            <Text size="xs" c="dimmed">{isProratedUpgrade ? 'Ajuste proporcional até o fim do ciclo atual' : 'Início de novo ciclo'}</Text>
                                        </Box>
                                        <Box style={{ textAlign: 'right' }}>
                                            <Title order={3}>{previewAvailable ? (isZeroCharge ? 'R$ 0,00' : formatCurrencyFromPreview(chargeNow)) : '--'}</Title>
                                            {paymentMethod === "credit" && checkoutStep === 'payment' && !isZeroCharge && previewAvailable && <Text size="xs" c="dimmed">{formData?.installments ?? 1} x {formatCurrencyFromPreview(chargeNow / (formData?.installments ?? 1))}</Text>}
                                        </Box>
                                    </Group>
                                </Stack>
                            </Paper>
                            <Paper p="md" shadow='xs' style={{ border: '1px solid #EFEFEF' }}>
                                <Group position='apart' align='flex-start'>
                                    <Box>
                                        <Text fw={700} size="sm">Cobrança no próximo ciclo</Text>
                                        <Text size="sm" c="dimmed" mt={4}>Valor completo da configuração final na renovação.</Text>
                                    </Box>
                                    <Title order={4}>{formatCurrencyFromPreview(nextCycleValue)}</Title>
                                </Group>
                            </Paper>
                            {isProratedUpgrade && <Paper p="md" shadow='xs' style={{ border: '1px solid #EFEFEF' }}>
                                <Group align='flex-start' spacing="sm" noWrap>
                                    <ThemeIcon color='yellow' variant='light' radius="xl"><FaCalendarAlt /></ThemeIcon>
                                    <Box>
                                        <Text fw={700} size="sm">Próxima renovação no valor completo da nova configuração</Text>
                                        <Text size="sm" c="dimmed" mt={4}>O ajuste proporcional cobre apenas o restante do ciclo atual. No próximo vencimento ({formatDate(activeUntil)}), a cobrança será integral do plano final.</Text>
                                    </Box>
                                </Group>
                            </Paper>}
                        </Stack>
                    </Paper>
                </Grid.Col>
                <Grid.Col md={12}>
                    <Group position='apart' mt="lg">
                        {checkoutStep === 'payment'
                            ? <Button variant='light' color='gray' onClick={() => setCheckoutStep('review')}>Voltar para revisão</Button>
                            : <div />}

                        {checkoutStep === 'review' && <Button disabled={shouldBlockCheckout || !termsAccepted} onClick={handleAdvanceToPayment} variant="filled" size="lg" rightIcon={<FaLongArrowAltRight />}>Continuar para pagamento</Button>}

                        {checkoutStep === 'payment' && paymentMethod !== "pix" && <Button disabled={shouldBlockCheckout || !termsAccepted || (!isZeroCharge && !formData?.payment?.method)} loading={loadingSave} onClick={finishSave} variant="filled" size="lg" rightIcon={<FaLongArrowAltRight />}>{isZeroCharge ? 'Confirmar atualização' : 'Finalizar pagamento'}</Button>}
                    </Group>
                </Grid.Col>
            </Grid>
        </>}
    </Box >;
}
